import React, { useContext, useState } from 'react';
import { AuthContext } from "../../context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { DateRangePicker } from 'rsuite';
import { addDays, addMonths, endOfDay, startOfDay, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, Legend, LinearScale, LineElement, PointElement, Tooltip } from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import { convertTimestampToFrenchFormat } from "../../scripts/utils";
import useWindowSize from "../../components/WindowSize";
import Loader from "../../components/Loader";
import { useTheme } from "../../context/ThemeContext";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);

const COLORS = {
    blue: "#1a57ff",
    statusGreen: "#AEC353",
    accentGreen: "#158864",
    red: "red",
    violet: "#997adb",
    orange: "rgb(250, 131, 52)",
    lightGray: "#d8dde6",
};

const MiniDoughnutChart = ({ labels, values, colors, centerLabel, theme }) => {
    const total = values.reduce((sum, value) => sum + value, 0);
    const chartValues = total > 0 ? values : [1];
    const chartColors = total > 0 ? colors : [theme === 'dark' ? 'rgba(255, 255, 255, 0.18)' : COLORS.lightGray];

    const data = {
        labels,
        datasets: [
            {
                data: chartValues,
                backgroundColor: chartColors,
                borderWidth: 0,
            }
        ]
    };

    const centerTextPlugin = {
        id: 'centerText',
        beforeDraw: (chart) => {
            const { ctx, chartArea } = chart;
            if (!chartArea) {
                return;
            }

            const centerX = (chartArea.left + chartArea.right) / 2;
            const centerY = (chartArea.top + chartArea.bottom) / 2;

            ctx.save();
            ctx.font = '600 12px Inter';
            ctx.fillStyle = theme === 'dark' ? '#ffffff' : '#11263c';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(centerLabel, centerX, centerY);
            ctx.restore();
        }
    };

    return (
        <div className="cato-overview-doughnut">
            <Doughnut
                key={`${theme}-${centerLabel}-${total}`}
                data={data}
                plugins={[centerTextPlugin]}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '72%',
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: total > 0,
                            backgroundColor: theme === 'dark' ? '#303030' : '#ffffff',
                            titleColor: theme === 'dark' ? '#ffffff' : '#11263c',
                            bodyColor: theme === 'dark' ? '#ffffff' : '#11263c',
                            borderColor: theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(17,38,60,0.12)',
                            borderWidth: 1
                        }
                    }
                }}
            />
        </div>
    );
};

const getLocale = (translationLocale) => translationLocale === 'fr-FR' ? 'fr-FR' : 'en-US';

const formatCompactNumber = (value, locale) => {
    return new Intl.NumberFormat(locale, {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1
    }).format(value || 0);
};

const formatBytes = (value, locale, digits = 1) => {
    if (!value) {
        return `0 B`;
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let size = value;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }

    return `${new Intl.NumberFormat(locale, {
        minimumFractionDigits: unitIndex === 0 ? 0 : 0,
        maximumFractionDigits: unitIndex >= 3 ? digits : 0
    }).format(size)} ${units[unitIndex]}`;
};

const formatPercent = (value, total, locale) => {
    if (!total) {
        return '0%';
    }

    return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
    }).format(value / total);
};

const formatShortDateLabel = (date, locale) => {
    return new Date(date).toLocaleDateString(locale, {
        day: '2-digit',
        month: 'short'
    });
};

const pad = (value) => String(value).padStart(2, '0');

const buildTimeFrame = (startDate, endDate) => {
    const startYear = startDate.getUTCFullYear();
    const startMonth = pad(startDate.getUTCMonth() + 1);
    const startDay = pad(startDate.getUTCDate());
    const startHours = pad(startDate.getUTCHours());
    const startMinutes = pad(startDate.getUTCMinutes());
    const startSeconds = pad(startDate.getUTCSeconds());

    const endYear = endDate.getUTCFullYear();
    const endMonth = pad(endDate.getUTCMonth() + 1);
    const endDay = pad(endDate.getUTCDate());
    const endHours = pad(endDate.getUTCHours());
    const endMinutes = pad(endDate.getUTCMinutes());
    const endSeconds = pad(endDate.getUTCSeconds());

    if (startYear === endYear && startMonth === endMonth) {
        return `utc.${startYear}-${startMonth}-{${startDay}/${startHours}:${startMinutes}:${startSeconds}--${endDay}/${endHours}:${endMinutes}:${endSeconds}}`;
    }

    return `utc.{${startYear}-${startMonth}-${startDay}/${startHours}:${startMinutes}:${startSeconds}--${endYear}-${endMonth}-${endDay}/${endHours}:${endMinutes}:${endSeconds}}`;
};

const getFieldValue = (fields, fieldName) => {
    const field = fields?.find((item) => item.name === fieldName);
    if (!field?.value) {
        return "";
    }

    const possibleKeys = ['string', 'date', 'name', 'id', 'type'];
    const matchedKey = possibleKeys.find((key) => field.value[key] !== undefined);

    if (matchedKey) {
        return field.value[matchedKey];
    }

    const firstValue = Object.values(field.value)[0];
    return typeof firstValue === 'string' ? firstValue : "";
};

const extractAppCategories = (appStats) => {
    if (!appStats) {
        return [];
    }

    const sources = [
        appStats.categories,
        appStats.categoryStats,
        appStats.records,
        appStats.apps,
        Array.isArray(appStats) ? appStats : null
    ].filter(Array.isArray);

    const categories = sources.flatMap((collection) =>
        collection.map((item, index) => ({
            label:
                item.category ||
                item.applicationCategory ||
                item.displayName ||
                item.name ||
                item.id ||
                `Category ${index + 1}`,
            value:
                item.bytes ||
                item.traffic ||
                item.totalTraffic ||
                item.totalBytes ||
                item.metrics?.bytes ||
                item.metrics?.traffic ||
                item.metrics?.totalBytes ||
                item.stats?.bytes ||
                0
        }))
    );

    return categories
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value);
};

export default function CatoOverview() {
    const { apiUrl } = useContext(AuthContext);
    const { t } = useTranslation();
    const { theme } = useTheme();
    const windowSize = useWindowSize();
    const today = endOfDay(new Date());
    const defaultStart = startOfDay(subDays(today, 29));
    const { afterToday } = DateRangePicker;
    const [date, setDate] = useState([defaultStart, today]);
    const locale = getLocale(t('en-EN'));
    const placement = windowSize.width < 945 ? 'bottomStart' : 'bottomEnd';

    const ranges = [
        {
            label: 'Aujourd\'hui',
            value: [startOfDay(new Date()), endOfDay(new Date())],
            placement: 'left'
        },
        {
            label: 'Hier',
            value: [startOfDay(addDays(new Date(), -1)), endOfDay(addDays(new Date(), -1))],
            placement: 'left'
        },
        {
            label: 'Les 7 derniers jours',
            value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
            placement: 'left'
        },
        {
            label: 'Les 30 derniers jours',
            value: [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())],
            placement: 'left'
        },
        {
            label: 'Ce mois-ci',
            value: [startOfMonth(new Date()), endOfDay(new Date())],
            placement: 'left'
        },
        {
            label: 'Le mois dernier',
            value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))],
            placement: 'left'
        }
    ];

    const startDate = Array.isArray(date) && date[0] ? startOfDay(date[0]) : defaultStart;
    const endDate = Array.isArray(date) && date[1] ? endOfDay(date[1]) : today;

    const payload = {
        timeFrame: buildTimeFrame(startDate, endDate),
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
    };

    const postCatoData = async (endpoint) => {
        try {
            const response = await axios.post(`${apiUrl}cato/${endpoint}`, payload, {
                withCredentials: true
            });
            return response.data;
        } catch (error) {
            if ([400, 404, 405, 422].includes(error.response?.status)) {
                const fallbackResponse = await axios.post(`${apiUrl}cato/${endpoint}`, {}, {
                    withCredentials: true
                });
                return fallbackResponse.data;
            }

            throw error;
        }
    };

    const accountMetricsQuery = useQuery({
        queryKey: ['cato-account-metrics-overview', payload.startDate, payload.endDate],
        queryFn: () => postCatoData('account_metrics')
    });

    const appStatsQuery = useQuery({
        queryKey: ['cato-app-stats-overview', payload.startDate, payload.endDate],
        queryFn: () => postCatoData('app_stats')
    });

    const auditFeedQuery = useQuery({
        queryKey: ['cato-audit-feed-overview', payload.startDate, payload.endDate],
        queryFn: () => postCatoData('audit_feed')
    });

    const accountMetrics = accountMetricsQuery.data?.data?.accountMetrics;
    const sites = accountMetrics?.sites || [];
    const siteRows = sites
        .map((site) => {
            const upstream = site.metrics?.bytesUpstream || 0;
            const downstream = site.metrics?.bytesDownstream || 0;

            return {
                id: site.id,
                upstream,
                downstream,
                total: upstream + downstream,
                interfaceCount: site.interfaces?.length || 0
            };
        })
        .sort((a, b) => b.total - a.total);

    const topSites = siteRows.slice(0, 5);
    const trafficChartRows = siteRows.slice(0, 8);
    const totalUpstream = siteRows.reduce((sum, site) => sum + site.upstream, 0);
    const totalDownstream = siteRows.reduce((sum, site) => sum + site.downstream, 0);
    const totalTraffic = totalUpstream + totalDownstream;
    const totalInterfaces = sites.reduce((sum, site) => sum + (site.interfaces?.length || 0), 0);
    const sitesWithRedundancy = sites.filter((site) => (site.interfaces?.length || 0) > 1).length;
    const singleLinkSites = Math.max(sites.length - sitesWithRedundancy, 0);

    const interfaceTypeCounts = sites.reduce((accumulator, site) => {
        (site.interfaces || []).forEach((item) => {
            const interfaceName = item.name?.toLowerCase() || '';
            if (interfaceName.includes('secondary')) {
                accumulator.secondary += 1;
            } else {
                accumulator.primary += 1;
            }
        });

        return accumulator;
    }, { primary: 0, secondary: 0 });

    const auditRecords = (auditFeedQuery.data?.data?.auditFeed?.accounts || [])
        .flatMap((account) => account.records || [])
        .sort((a, b) => new Date(b.time) - new Date(a.time));

    const changeTypeCounts = auditRecords.reduce((accumulator, record) => {
        const changeType = getFieldValue(record.fields, 'change_type') || 'OTHER';
        accumulator[changeType] = (accumulator[changeType] || 0) + 1;
        return accumulator;
    }, {});

    const appCategories = extractAppCategories(appStatsQuery.data?.data?.appStats);
    const topAppCategories = appCategories.slice(0, 4);
    const totalAppTraffic = appCategories.reduce((sum, category) => sum + category.value, 0);
    const hasTrafficData = trafficChartRows.length > 0;
    const hasAppData = topAppCategories.length > 0;
    const hasAuditData = auditRecords.length > 0;
    const trafficChartSource = hasTrafficData
        ? trafficChartRows
        : [
            { id: 'start', upstream: 0, downstream: 0, total: 0, interfaceCount: 0 },
            { id: 'end', upstream: 0, downstream: 0, total: 0, interfaceCount: 0 }
        ];

    const chartTextColor = theme === 'dark' ? '#ffffff' : '#5f6b7a';
    const chartGridColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(17, 38, 60, 0.08)';

    const trafficChartData = {
        labels: trafficChartSource.map((site) => site.id),
        datasets: [
            {
                label: t('Upstream'),
                data: trafficChartSource.map((site) => site.upstream),
                backgroundColor: COLORS.blue,
                borderColor: COLORS.blue,
                borderWidth: 3,
                pointRadius: 0,
                pointHitRadius: 18,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: COLORS.blue,
                pointHoverBorderColor: '#ffffff',
                pointHoverBorderWidth: 2,
                fill: false,
                tension: 0.35,
            },
            {
                label: t('Downstream'),
                data: trafficChartSource.map((site) => site.downstream),
                backgroundColor: COLORS.orange,
                borderColor: COLORS.orange,
                borderWidth: 3,
                pointRadius: 0,
                pointHitRadius: 18,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: COLORS.orange,
                pointHoverBorderColor: '#ffffff',
                pointHoverBorderWidth: 2,
                fill: false,
                tension: 0.35,
            }
        ]
    };

    const trafficChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: theme === 'dark' ? '#303030' : '#ffffff',
                titleColor: theme === 'dark' ? '#ffffff' : '#11263c',
                bodyColor: theme === 'dark' ? '#ffffff' : '#11263c',
                footerColor: theme === 'dark' ? '#d0d1d2' : '#5f6b7a',
                borderColor: theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(17,38,60,0.12)',
                borderWidth: 1,
                displayColors: true,
                callbacks: {
                    title: (tooltipItems) => `${t('Site')} ${tooltipItems[0].label}`,
                    label: (context) => `${context.dataset.label}: ${formatBytes(context.raw, locale, 2)}`,
                    footer: (tooltipItems) => {
                        const dataIndex = tooltipItems[0]?.dataIndex ?? 0;
                        const currentSite = trafficChartSource[dataIndex];

                        if (!currentSite) {
                            return '';
                        }

                        return [
                            `${t('Total Traffic')}: ${formatBytes(currentSite.total, locale, 2)}`,
                            `${t('Interfaces')}: ${currentSite.interfaceCount}`
                        ];
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    display: false
                },
                grid: {
                    display: false
                },
                border: {
                    display: false
                }
            },
            y: {
                ticks: {
                    color: chartTextColor,
                    maxTicksLimit: 5,
                    callback: (value) => formatBytes(value, locale, 0)
                },
                grid: {
                    color: chartGridColor
                },
                border: {
                    display: false
                }
            }
        },
        interaction: {
            mode: 'index',
            intersect: false
        }
    };

    const overviewCards = [
        {
            title: t('Traffic'),
            value: formatBytes(totalTraffic, locale, 1),
            chartLabel: formatCompactNumber(siteRows.length, locale),
            topBorder: `linear-gradient(90deg, ${COLORS.blue} 0%, ${COLORS.orange} 100%)`,
            labels: [t('Upstream'), t('Downstream')],
            values: [totalUpstream, totalDownstream],
            colors: [COLORS.blue, COLORS.orange],
            legend: [
                { label: t('Upstream'), value: formatBytes(totalUpstream, locale, 1), color: COLORS.blue },
                { label: t('Downstream'), value: formatBytes(totalDownstream, locale, 1), color: COLORS.orange }
            ]
        },
        {
            title: t('Sites'),
            value: formatCompactNumber(sites.length, locale),
            chartLabel: formatCompactNumber(sites.length, locale),
            topBorder: `linear-gradient(90deg, ${COLORS.violet} 0%, ${COLORS.blue} 100%)`,
            labels: [t('Sites with Redundancy'), t('Single Link Sites')],
            values: [sitesWithRedundancy, singleLinkSites],
            colors: [COLORS.violet, COLORS.blue],
            legend: [
                { label: t('Sites with Redundancy'), value: sitesWithRedundancy, color: COLORS.violet },
                { label: t('Single Link Sites'), value: singleLinkSites, color: COLORS.blue }
            ]
        },
        {
            title: t('Interfaces'),
            value: formatCompactNumber(totalInterfaces, locale),
            chartLabel: formatCompactNumber(totalInterfaces, locale),
            topBorder: `linear-gradient(90deg, ${COLORS.statusGreen} 0%, ${COLORS.violet} 100%)`,
            labels: [t('Primary Interfaces'), t('Secondary Interfaces')],
            values: [interfaceTypeCounts.primary, interfaceTypeCounts.secondary],
            colors: [COLORS.statusGreen, COLORS.violet],
            legend: [
                { label: t('Primary Interfaces'), value: interfaceTypeCounts.primary, color: COLORS.statusGreen },
                { label: t('Secondary Interfaces'), value: interfaceTypeCounts.secondary, color: COLORS.violet }
            ]
        },
        {
            title: t('Events'),
            value: formatCompactNumber(auditRecords.length, locale),
            chartLabel: formatCompactNumber(auditRecords.length, locale),
            topBorder: `linear-gradient(90deg, ${COLORS.statusGreen} 0%, ${COLORS.blue} 50%, ${COLORS.violet} 100%)`,
            labels: [t('Created'), t('Modified'), t('Other Changes')],
            values: [changeTypeCounts.CREATED || 0, changeTypeCounts.MODIFIED || 0, Math.max(auditRecords.length - (changeTypeCounts.CREATED || 0) - (changeTypeCounts.MODIFIED || 0), 0)],
            colors: [COLORS.statusGreen, COLORS.blue, COLORS.violet],
            legend: [
                { label: t('Created'), value: changeTypeCounts.CREATED || 0, color: COLORS.statusGreen },
                { label: t('Modified'), value: changeTypeCounts.MODIFIED || 0, color: COLORS.blue },
                { label: t('Other Changes'), value: Math.max(auditRecords.length - (changeTypeCounts.CREATED || 0) - (changeTypeCounts.MODIFIED || 0), 0), color: COLORS.violet }
            ]
        }
    ];

    const isLoading = accountMetricsQuery.isLoading || auditFeedQuery.isLoading || appStatsQuery.isLoading;

    if (isLoading) {
        return (
            <section className="cato-overview cato-overview-loading">
                <Loader />
            </section>
        );
    }

    return (
        <section className="cato-overview">
            <div className="cato-overview-header">
                <div className="cato-overview-header-title">
                    <h2>Overview</h2>
                </div>
                <div className="cato-overview-header-actions">
                    <DateRangePicker
                        ranges={ranges}
                        cleanable={false}
                        character=" / "
                        value={date}
                        onChange={(nextDate) => nextDate && setDate(nextDate)}
                        format="dd-MM-yyyy"
                        shouldDisableDate={afterToday()}
                        defaultValue={[defaultStart, today]}
                        locale={{
                            sunday: t('Su'),
                            monday: t('Mo'),
                            tuesday: t('Tu'),
                            wednesday: t('We'),
                            thursday: t('Th'),
                            friday: t('Fr'),
                            saturday: t('Sa'),
                            hours: t('Hours'),
                        }}
                        placement={placement}
                    />
                </div>
            </div>

            <>
                <div className="cato-overview-cards">
                    {overviewCards.map((card) => (
                        <article className="cato-overview-card" key={card.title}>
                            <div className="cato-overview-card-top-border" style={{ background: card.topBorder }}></div>
                            <h3>{card.title}</h3>
                            <div className="cato-overview-card-content">
                                <div className="cato-overview-card-value">
                                    <strong>{card.value}</strong>
                                </div>
                                <MiniDoughnutChart
                                    labels={card.labels}
                                    values={card.values}
                                    colors={card.colors}
                                    centerLabel={card.chartLabel}
                                    theme={theme}
                                />
                                <div className="cato-overview-card-legend">
                                    {card.legend.map((item) => (
                                        <div className="cato-overview-card-legend-item" key={item.label}>
                                            <span className="cato-overview-card-legend-label">
                                                <span className="cato-overview-card-legend-dot" style={{ backgroundColor: item.color }}></span>
                                                {item.label}
                                            </span>
                                            <span className="cato-overview-card-legend-value">{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="cato-overview-section-header">
                    <h3>{t('Network')}</h3>
                </div>

                <div className="cato-overview-grid">
                    <article className="cato-overview-panel cato-overview-panel-large">
                        <div className="cato-overview-panel-header">
                            <div>
                                <h3>{t('Traffic by Site')}</h3>
                                <p>{t('Cumulative traffic over selected period')}</p>
                            </div>
                        </div>
                        <div className="cato-overview-traffic-content">
                            <div className="cato-overview-traffic-main">
                                <div className="cato-overview-traffic-summary">
                                    <strong>{formatBytes(totalTraffic, locale, 1)}</strong>
                                    <span>{t('Total for all accounts')}</span>
                                </div>
                                <div className="cato-overview-traffic-chart">
                                    <div className="cato-overview-traffic-chart-canvas">
                                        <Line data={trafficChartData} options={trafficChartOptions} />
                                    </div>
                                    <div className="cato-overview-chart-dates">
                                        <span>{formatShortDateLabel(startDate, locale)}</span>
                                        <span>{formatShortDateLabel(endDate, locale)}</span>
                                    </div>
                                    {!hasTrafficData && (
                                        <div className="cato-overview-empty-text cato-overview-empty-text-inline">
                                            {t('No traffic data available for the selected period')}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="cato-overview-top-sites">
                                <h4>{t('Top Sites by Traffic')}</h4>
                                {topSites.length > 0 ? (
                                    <ul>
                                        {topSites.map((site) => (
                                            <li key={site.id}>
                                                <span>{t('Site')} {site.id}</span>
                                                <strong>{formatBytes(site.total, locale, 1)}</strong>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="cato-overview-empty-text">
                                        {t('Aucune donnée disponible')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </article>

                    <article className="cato-overview-panel">
                        <div className="cato-overview-panel-header">
                            <div>
                                <h3>{t('Application Categories by Traffic')}</h3>
                                <p>{t('Applications')}</p>
                            </div>
                            <div className="cato-overview-panel-highlight">
                                <strong>{formatCompactNumber(appCategories.length, locale)}</strong>
                                <span>{t('Total categories')}</span>
                            </div>
                        </div>
                        {hasAppData ? (
                            <div className="cato-overview-apps">
                                <div className="cato-overview-apps-bar">
                                    {topAppCategories.map((category, index) => (
                                        <span
                                            key={category.label}
                                            style={{
                                                width: `${(category.value / totalAppTraffic) * 100}%`,
                                                backgroundColor: [COLORS.accentGreen, COLORS.statusGreen, COLORS.orange, COLORS.violet][index % 4]
                                            }}
                                        ></span>
                                    ))}
                                </div>
                                <ul className="cato-overview-apps-list">
                                    {topAppCategories.map((category, index) => (
                                        <li key={category.label}>
                                            <span className="cato-overview-apps-list-name">
                                                <span
                                                    className="cato-overview-card-legend-dot"
                                                    style={{ backgroundColor: [COLORS.accentGreen, COLORS.statusGreen, COLORS.orange, COLORS.violet][index % 4] }}
                                                ></span>
                                                {category.label}
                                            </span>
                                            <span>{formatBytes(category.value, locale, 1)}</span>
                                            <strong>{formatPercent(category.value, totalAppTraffic, locale)}</strong>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="cato-overview-empty-state">
                                {appStatsQuery.isError ? t('Application data unavailable') : t('Aucune donnée disponible')}
                            </div>
                        )}
                    </article>
                </div>

                <article className="cato-overview-panel cato-overview-panel-audit">
                    <div className="cato-overview-panel-header">
                        <div>
                            <h3>{t('Recent Audit Activity')}</h3>
                            <p>{t('Latest events')}</p>
                        </div>
                        <div className="cato-overview-panel-highlight">
                            <strong>{formatCompactNumber(auditRecords.length, locale)}</strong>
                            <span>{t('Audit records')}</span>
                        </div>
                    </div>
                    {hasAuditData ? (
                        <div className="cato-overview-audit-list">
                            {auditRecords.slice(0, 5).map((record, index) => {
                                const action = getFieldValue(record.fields, 'change_type') || t('Unknown action');
                                const module = getFieldValue(record.fields, 'module') || t('Unknown module');
                                const objectName = record.object?.name || getFieldValue(record.fields, 'model_name') || t('Unknown object');
                                const modelType = getFieldValue(record.fields, 'model_type');
                                const admin = record.admin?.name || getFieldValue(record.fields, 'admin');
                                const badgeClass =
                                    action === 'CREATED' ? 'is-created' :
                                        action === 'MODIFIED' ? 'is-modified' :
                                            'is-other';

                                return (
                                    <div className="cato-overview-audit-item" key={`${record.time}-${index}`}>
                                        <div className="cato-overview-audit-item-main">
                                            <div className="cato-overview-audit-item-top">
                                                <span className={`cato-overview-audit-badge ${badgeClass}`}>{action}</span>
                                                <strong>{objectName}</strong>
                                            </div>
                                            <p>{modelType ? `${modelType} - ${module}` : module}</p>
                                            <span>{admin}</span>
                                        </div>
                                        <div className="cato-overview-audit-item-time">
                                            {convertTimestampToFrenchFormat(record.time, locale)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="cato-overview-empty-state">
                            {auditFeedQuery.isError ? t('No audit activity available for the selected period') : t('Aucune donnée disponible')}
                        </div>
                    )}
                </article>
            </>
        </section>
    );
}
